/* eslint-disable react-hooks/set-state-in-effect */
import { useCallback, useEffect, useRef, useState } from "react";

import {
  generateSurveyQuestions,
  useSurveyGenerationStatus,
} from "../api/experimentsApi";

interface UseGenerateSurveyQuestionsOptions {
  experimentId?: string;
  iterationId?: string;
  refetchFeedbackFlow: () => void;
  isPreviewReady: boolean;
}

interface UseGenerateSurveyQuestionsReturn {
  isGenerating: boolean;
  generationProgress: number;
  generationStage: string | null;
  generationError: string | null;
  dismissGenerationError: () => void;
}

export function useGenerateSurveyQuestions({
  experimentId,
  iterationId,
  refetchFeedbackFlow,
  isPreviewReady,
}: UseGenerateSurveyQuestionsOptions): UseGenerateSurveyQuestionsReturn {
  const [jobId, setJobId] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [isErrorDismissed, setIsErrorDismissed] = useState(false);
  const hasTriggeredRef = useRef(false);
  const hasPendingRefetchRef = useRef(false);
  const isPreviewReadyRef = useRef(isPreviewReady);
  useEffect(() => {
    isPreviewReadyRef.current = isPreviewReady;
  }, [isPreviewReady]);

  // Poll for job status
  const statusQuery = useSurveyGenerationStatus(
    experimentId,
    iterationId,
    jobId ?? undefined,
    isPolling,
  );

  // React to terminal status changes (stop polling, trigger side effects)
  useEffect(() => {
    if (!statusQuery.data || !isPolling) return;

    const { status } = statusQuery.data;
    if (status === "Completed") {
      setIsPolling(false);
      if (isPreviewReadyRef.current) {
        refetchFeedbackFlow();
      } else {
        hasPendingRefetchRef.current = true;
      }
    } else if (status === "Failed") {
      setIsPolling(false);
    }
  }, [statusQuery.data, isPolling, refetchFeedbackFlow]);

  // Deferred refetch when preview becomes ready
  useEffect(() => {
    if (isPreviewReady && hasPendingRefetchRef.current) {
      hasPendingRefetchRef.current = false;
      refetchFeedbackFlow();
    }
  }, [isPreviewReady, refetchFeedbackFlow]);

  // Auto-trigger: fire POST on mount when IDs are present
  useEffect(() => {
    if (!experimentId || !iterationId || hasTriggeredRef.current) return;
    hasTriggeredRef.current = true;
    setIsRequesting(true);

    generateSurveyQuestions(experimentId, iterationId)
      .then((data) => {
        setJobId(data.jobId);
        setIsErrorDismissed(false);
        setIsRequesting(false);

        if (data.status === "Completed") {
          if (isPreviewReadyRef.current) {
            refetchFeedbackFlow();
          } else {
            hasPendingRefetchRef.current = true;
          }
        } else if (
          data.status === "Queued" ||
          data.status === "Processing" ||
          data.status === "Failed"
        ) {
          setIsPolling(true);
        }
      })
      .catch((error: Error) => {
        setIsRequesting(false);
        setMutationError(
          error.message || "Failed to start survey generation",
        );
      });
  }, [experimentId, iterationId, refetchFeedbackFlow]);

  const isGenerating = isRequesting || isPolling;

  // Derive error from query data or mutation error — no useEffect sync
  const pollingError =
    statusQuery.data?.status === "Failed"
      ? statusQuery.data.errorMessage || "Survey generation failed"
      : null;
  const generationError = isErrorDismissed
    ? null
    : (pollingError ?? mutationError);

  const dismissGenerationError = useCallback(() => {
    setIsErrorDismissed(true);
    setMutationError(null);
  }, []);

  return {
    isGenerating,
    generationProgress: statusQuery.data?.progress ?? 0,
    generationStage: statusQuery.data?.currentStage ?? null,
    generationError,
    dismissGenerationError,
  };
}
